import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { styles } from './styles';
import { injectGlobalCSS } from './globalStyles';

import IntroSequence from './components/introSequence/introSequence';
import HeroSignature from './components/heroSignature/heroSignature';
import Bookend from './components/bookend/bookend';
import Vae from './components/vae/vae';
import Intro from './pages/intro/intro';
import Services from './pages/services/services';
import Products from './pages/products/products';
import About from './pages/about/about';
import Contact from './pages/contact/contact';
import Legal from './pages/legal/legal';
import NotFound from './pages/notFound/notFound';
import Privacy from './pages/privacy/privacy';

// The site is one page; /legal is the one exception, and it gets a path check rather than a
// router.
//
// Firebase Hosting already rewrites every path to /index.html (see firebase.json), so the
// route arrives here intact and a small branch is the whole mechanism. react-router would be
// a new dependency for one document page — that is the trade, and it is not close. If the
// site ever grows a third addressable route, revisit it then rather than now.
//
// /legal IS HOST-SCOPED. One Hosting site serves every domain attached to it, so a path-only
// check would render PlayerCafe's Terms and Privacy Policy on novaeon.io/legal too — another
// brand's documents under the Novaeon name. Each host maps to its own Firestore document, and
// a host with no entry has no legal page: it falls through to the marketing page.
const LEGAL_DOCUMENT_BY_HOST: Record<string, string> = {
  'playercafe.novaeon.io': 'playerCafe',
  // Dev counterpart of the line above: http://playercafe.localhost:3000/legal. Browsers
  // resolve *.localhost to the loopback address themselves (RFC 6761), so this needs no
  // /etc/hosts entry and no dev-server config.
  //
  // Plain `localhost` is deliberately ABSENT. Mapping it would make every brand's documents
  // reachable at localhost:3000/legal, which is exactly the host-agnostic behaviour this map
  // exists to prevent — and it would mean dev never exercises the hostname branch that
  // production depends on.
  'playercafe.localhost': 'playerCafe',
};

const legalDocumentId = (): string | undefined =>
  LEGAL_DOCUMENT_BY_HOST[window.location.hostname];

// Novaeon's own privacy policy. NOT host-scoped, unlike /legal: Novaeon operates
// playercafe.novaeon.io too, this policy governs that host, and the marketing page served at
// the subdomain's root links here from its footer.
const PRIVACY_PATH = '/privacy';

// The marketing page's own paths. `/index.html` is here because Hosting serves that file
// directly rather than rewriting it, so React sees it verbatim in the pathname.
const HOME_PATHS = ['', '/index.html'];

// Trailing slashes stripped so /legal and /legal/ are the same route; '' is the root.
const currentPath = (): string => window.location.pathname.replace(/\/+$/, '');

const App: React.FC = () => {
  useEffect(() => {
    injectGlobalCSS();
  }, []);

  const path = currentPath();

  // The legal documents are a standalone page — no hero, no intro sequence, no VAE widget.
  // Someone arriving here from the app, or from App Review, came to read a document.
  if (path === '/legal') {

    const legalId = legalDocumentId();

    // A host with no mapped document has no legal page. That is a 404, NOT a silent fall-back
    // to the marketing page — quietly serving something else for a URL that does not exist
    // is how a broken link goes unnoticed.
    return legalId ? <Legal productId={legalId} /> : <NotFound />;
  }

  if (path === PRIVACY_PATH) {
    return <Privacy />;
  }

  if (!HOME_PATHS.includes(path)) {
    return <NotFound />;
  }

  return (
    <div style={styles.app}>
      <div className="hero-signature" style={styles.signatureLayer}>
        <HeroSignature />
      </div>

      <IntroSequence />
      <Bookend isFooter={false} />

      <main>
        <Intro />
        <Services />
        <Products />
        <About />
        <Contact />
      </main>

      <Bookend isFooter={true} />

      <Vae />
    </div>
  );
};

// Inject the theme variables + base styles before first paint (avoids a flash).
injectGlobalCSS();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<App />);
